import { z } from "zod";
// ── Shared ──────────────────────────────────────────────────────────
export const passwordSchema = z
    .string()
    .min(8, "Password must be at least 8 characters")
    .max(256, "Password is too long");
export const okResponse = z.object({
    ok: z.literal(true),
    /** Present only in dev when email send was skipped due to unset webhook URL. */
    devLink: z.string().url().optional(),
});
// ── Existing Phase 3 ───────────────────────────────────────────────
export const loginRequest = z.object({
    email: z.string().email(),
    // Same strength rule as signup/reset for consistency. The web login form
    // mirrors it (min 8) so a too-short password is caught client-side as a
    // field error rather than surfacing as a server 400.
    password: passwordSchema,
});
/**
 * Per-request accessible-store resolution for the caller's role.
 *   "all"    — tenant-wide role (tenant_admin / ops_manager / viewer);
 *              no per-store gating on the client.
 *   "scoped" — store-scoped role; storeIds is the active set the client
 *              must use for UI gating (e.g. empty -> show empty state).
 */
export const accessibleStores = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("all") }),
    z.object({ mode: z.literal("scoped"), storeIds: z.array(z.string().uuid()) }),
]);
export const publicUser = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    // Tenant-less users (0 companies) authenticate without a tenant; both are null.
    tenantId: z.string().uuid().nullable(),
    // Human-readable unique tenant code — used as the URL slug (/t/<code>/…).
    // Null for tenant-less users.
    tenantCode: z.string().nullable(),
    role: z.string().nullable(),
    // Tenant-less users see no accessibleStores either; nullable in the same way.
    accessibleStores: accessibleStores.nullable(),
});
export const loginResponse = z.object({
    user: publicUser,
    csrfToken: z.string(),
});
// `/auth/me` mints a fresh CSRF token on every call so the in-memory token
// survives page refreshes (auth cookie is httpOnly and persists; CSRF token
// lives only in JS memory). Without this, the first mutation after refresh
// returns 403 "Invalid CSRF token".
export const meResponse = z.object({
    user: publicUser,
    csrfToken: z.string(),
});
export const refreshResponse = z.object({
    csrfToken: z.string(),
});
export const logoutResponse = z.object({
    ok: z.literal(true),
});
// ── 4B-1 additions: signup flow ─────────────────────────────────────
export const signupStartRequest = z.object({
    email: z.string().email().max(255),
    password: passwordSchema,
    firstName: z.string().min(1).max(255),
    lastName: z.string().min(1).max(255),
    /**
     * Optional invitation token. When it's a valid pending invite for THIS email,
     * the server skips the verification email and auto-verifies + logs the user
     * in (the invite was delivered to this address, which proves ownership) — so
     * an invited signup lands straight back on the accept page instead of losing
     * the invite across the email-verification hop.
     */
    invitationToken: z.string().min(16).max(255).optional(),
});
// Either an email was sent (normal signup) OR the invited signup was
// auto-verified and logged in (user + csrfToken set, like loginResponse).
export const signupStartResponse = z.object({
    ok: z.literal(true),
    verified: z.boolean().default(false),
    user: publicUser.optional(),
    csrfToken: z.string().optional(),
    devLink: z.string().optional(),
});
export const signupVerifyRequest = z.object({
    token: z.string().min(16).max(255),
});
export const resendVerificationRequest = z.object({
    email: z.string().email().max(255),
    /** Carry a matching pending invite in the verification email, including across browsers. */
    invitationToken: z.string().min(16).max(255).optional(),
});
// ── 4B-1 additions: password reset flow ─────────────────────────────
export const forgotPasswordRequest = z.object({
    email: z.string().email().max(255),
});
export const resetPasswordRequest = z.object({
    token: z.string().min(16).max(255),
    newPassword: passwordSchema,
});
// ── 4B-1 additions: sessions ────────────────────────────────────────
export const sessionInfo = z.object({
    id: z.string().uuid(), // auth_sessions.public_id
    ipAddress: z.string().nullable(),
    userAgent: z.string().nullable(),
    lastSeenAt: z.string().datetime(),
    createdAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable(),
    isCurrent: z.boolean(),
});
export const sessionsListResponse = z.object({
    items: z.array(sessionInfo),
});
// ── 4B-2 additions: tenant switcher ─────────────────────────────────
export const switchTenantRequest = z.object({
    tenantId: z.string().uuid(),
});
// Reuses loginResponse shape
export const switchTenantResponse = loginResponse;
//# sourceMappingURL=auth.schemas.js.map